/**
 * Hand a Cogni / Keyword search the user just ran to the conversation.
 *
 * The missing half of the handoff. Investigator writes rows under a job id, so
 * attaching one is a pointer; these two produce a response that is rendered
 * and discarded, so without this the user could drive them manually and CEPI
 * stayed blind — the exact fork the capability cards exist to close.
 *
 * The RESPONSE is posted, not the request. Cogni reranks with an LLM, so
 * asking the backend to re-run would attach a different set than the one on
 * screen, and "CEPI reads exactly what you did" is the whole promise.
 */
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from './impersonationHeaders';
import type { Capability } from '../types/qa';
import type { CogniCorpus } from '../shell/runResults';
import type { KeywordSearchRequest, KeywordSearchResponse } from '../types';
import {
  isPassagesUnavailable, keywordPassagesQueryOptions, mergePassages,
} from '../hooks/useKeywordSearch';

const API_BASE_URL = import.meta.env.VITE_API_URL ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

export interface CaptureResult {
  threadId?: string;
  rows?: number;
  /** False for every Cogni set, and for a Keyword census that was windowed. */
  provesAbsence?: boolean;
  error?: string;
}

export async function captureResultSet(args: {
  capability: Capability;
  /** Cogni only. Absent means labels — which is what the backend assumes for
   *  a client that does not send it. */
  corpus?: CogniCorpus | undefined;
  queryText: string;
  /** The request that produced it — provenance for "complete over what". */
  request: unknown;
  /** The response body as received. */
  response: unknown;
  threadId: string | null;
}): Promise<CaptureResult> {
  if (args.capability === 'investigator') {
    // Investigator has its own attach path against a real job id; routing it
    // here would create a second, parallel notion of "the active result".
    return { error: 'Investigator runs attach by job, not by capture.' };
  }
  try {
    const response = args.capability === 'keyword'
      ? await withKeywordPassages(args.request, args.response)
      : args.response;
    const user = auth.currentUser;
    const token = user ? await user.getIdToken(false) : null;
    const res = await fetch(`${API_BASE_URL}/v2/research/result-sets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...getImpersonationHeaders(),
      },
      body: JSON.stringify({
        capability: args.capability,
        ...(args.corpus ? { corpus: args.corpus } : {}),
        query_text: args.queryText,
        request: args.request ?? {},
        response: response ?? {},
        ...(args.threadId ? { thread_id: args.threadId } : {}),
      }),
    });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        const body = await res.json();
        if (typeof body?.detail === 'string') detail = body.detail;
      } catch { /* non-JSON error body */ }
      return { error: detail };
    }
    const body = await res.json();
    return {
      threadId: body?.thread_id,
      rows: body?.rows,
      provesAbsence: body?.proves_absence,
    };
  } catch (err) {
    if (isPassagesUnavailable(err)) {
      return { error: 'Passages for these results are unavailable right now. Try again shortly.' };
    }
    return { error: 'Could not reach the server.' };
  }
}

/**
 * An on-demand keyword page carries rows WITHOUT highlights (they are fetched
 * when a row is expanded), so capturing it as-is would hand CEPI drug names
 * and no text. Fill the passages for exactly those rows first, the same way
 * Export does. Any other response is returned unchanged.
 */
async function withKeywordPassages(request: unknown, response: unknown): Promise<unknown> {
  const page = response as KeywordSearchResponse | undefined;
  if (page?.passages_on_demand !== true || !page.results?.length) return response;
  const passages = await keywordPassagesQueryOptions(
    request as KeywordSearchRequest, page.results.map((r) => r.drug_id),
  ).queryFn();
  return { ...page, results: mergePassages(page.results, passages) };
}

export default captureResultSet;
